/**
 * Реестр навигации кабинета: раздел (`nav.sections` из `me/context`) → подпись, иконка, маршрут.
 *
 * Состав разделов считает сервер по роли (client-cabinet-shell / team-portal); фронт лишь
 * раскрывает идентификаторы в пункты меню. Один идентификатор (`orders`, `documents`) означает
 * разное у клиента и команды, поэтому маппинг выбирается по «стороне» роли.
 */
import {
  AlertTriangle,
  Bell,
  Building2,
  CircleHelp,
  ClipboardList,
  FileCheck2,
  FileClock,
  FileText,
  FlaskConical,
  GraduationCap,
  Handshake,
  HardHat,
  Home,
  Leaf,
  MessageCircleQuestion,
  ScrollText,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { Role } from '@/lib/api'

export interface NavItemDef {
  /** Идентификатор раздела из nav.sections. */
  section: string
  label: string
  icon: LucideIcon
  path: string
  /**
   * Подпункты 2-го уровня (p2-ot-section, design D1) — всегда развёрнуты, без сворачивания.
   * Сейчас единственный обладатель — «ot» (Охрана труда → Обучение/Спецоценка/Профриски).
   */
  children?: Omit<NavItemDef, 'children'>[]
}

/** Команда Центрики (внутренние роли) vs клиент (внешние). */
export function isTeamRole(role: Role): boolean {
  return role === 'admin' || role === 'head' || role === 'employee'
}

// Определения «Обучение»/«Спецоценка»/«Профриски» — вынесены в константы, чтобы запись `ot`
// ниже ПЕРЕИСПОЛЬЗОВАЛА их как подпункты 2-го уровня, не дублируя текст/иконку/путь (design D1).
const TRAINING_ITEM: Omit<NavItemDef, 'section'> = {
  label: 'Обучение',
  icon: GraduationCap,
  path: '/training',
}
const SOUT_ITEM: Omit<NavItemDef, 'section'> = { label: 'Спецоценка', icon: ShieldCheck, path: '/sout' }
const PROFRISKS_ITEM: Omit<NavItemDef, 'section'> = {
  label: 'Профриски',
  icon: AlertTriangle,
  path: '/profrisks',
}

/**
 * Клиентская сторона: home/ot/orders/staff/documents/settings/help (фаза 2, p2-ot-section §9.1,
 * design D1; help-page) — «training»/«sout»/«profrisks» больше НЕ строятся как отдельные пункты
 * 1-го уровня для company (они — подразделы «ot», см. `resolveNavItems`), но остаются валидными
 * ключами реестра — физлицо (`individual`) продолжает получать «training» как плоский пункт.
 * «help» сервер отдаёт только company (help-page) — контент страницы про сценарии
 * клиента-компании, у физлица его нет.
 */
const CLIENT_NAV: Record<string, Omit<NavItemDef, 'section'>> = {
  home: { label: 'Главная', icon: Home, path: '/' },
  training: TRAINING_ITEM,
  sout: SOUT_ITEM,
  profrisks: PROFRISKS_ITEM,
  ot: {
    label: 'Охрана труда',
    icon: HardHat,
    path: '/ot',
    children: [
      { section: 'training', ...TRAINING_ITEM },
      { section: 'sout', ...SOUT_ITEM },
      { section: 'profrisks', ...PROFRISKS_ITEM },
    ],
  },
  // «Экология» (ecology-sites, вариант Б) — обзор с площадками и подразделы по образцу «ot».
  ecology: {
    label: 'Экология',
    icon: Leaf,
    path: '/ecology',
    children: [
      {
        section: 'ecology-projects',
        label: 'Проектные работы',
        icon: FileCheck2,
        path: '/ecology/projects',
      },
      { section: 'ecology-reports', label: 'Отчётность', icon: FileClock, path: '/ecology/reports' },
      { section: 'ecology-lab', label: 'Лабораторный контроль', icon: FlaskConical, path: '/ecology/lab' },
      {
        section: 'ecology-subscription',
        label: 'Абонентское обслуживание',
        icon: Handshake,
        path: '/ecology/subscription',
      },
    ],
  },
  orders: { label: 'Мои заказы', icon: ClipboardList, path: '/orders' },
  // «Мои вопросы» (client-questions; ТЗ п.9.7: «под „Мои заказы“») — и у компании, и у физлица.
  questions: { label: 'Мои вопросы', icon: MessageCircleQuestion, path: '/questions' },
  staff: { label: 'Сотрудники', icon: Users, path: '/staff' },
  documents: { label: 'Документы', icon: FileText, path: '/documents' },
  settings: { label: 'Настройки', icon: Settings, path: '/settings' },
  help: { label: 'Инструкция', icon: CircleHelp, path: '/help' },
}

/**
 * Командная сторона: home/orders(очередь)/clients/training(журнал)/ecology/accounts/documents/audit/
 * notifications/settings. «ecology» (ecology-catalog) — настройки раздела «Экология», сервер отдаёт
 * его админу.
 */
const TEAM_NAV: Record<string, Omit<NavItemDef, 'section'>> = {
  home: { label: 'Главная', icon: Home, path: '/' },
  orders: { label: 'Очередь заказов', icon: ClipboardList, path: '/team/orders' },
  questions: { label: 'Вопросы клиентов', icon: MessageCircleQuestion, path: '/team/questions' },
  clients: { label: 'Клиенты', icon: Building2, path: '/team/clients' },
  training: { label: 'Журнал обучения', icon: GraduationCap, path: '/team/training' },
  ecology: { label: 'Экология', icon: Leaf, path: '/team/ecology' },
  accounts: { label: 'Аккаунты', icon: UserCog, path: '/team/accounts' },
  documents: { label: 'Документы', icon: FileText, path: '/team/documents' },
  audit: { label: 'Журнал действий', icon: ScrollText, path: '/team/audit' },
  notifications: { label: 'Уведомления', icon: Bell, path: '/team/notifications' },
  settings: { label: 'Настройки', icon: Settings, path: '/settings' },
}

/**
 * Идентификаторы справочных (не рабочих) разделов, визуально отделённых разделителем в
 * сайдбаре — «Настройки» и «Инструкция» (help-page). Порядок среди них — как в `nav.sections` от
 * сервера (см. `navByRole`, backend/internal/http/handlers_me.go).
 */
export const BELOW_DIVIDER_SECTIONS: ReadonlySet<string> = new Set(['settings', 'help'])

/**
 * Разворачивает `sections` в упорядоченные пункты меню согласно роли. Неизвестные разделы
 * (нет в реестре стороны) пропускаются — это fail-safe на случай новых серверных разделов.
 */
export function resolveNavItems(role: Role, sections: string[]): NavItemDef[] {
  const registry = isTeamRole(role) ? TEAM_NAV : CLIENT_NAV
  const items: NavItemDef[] = []
  for (const section of sections) {
    const def = registry[section]
    if (def) items.push({ section, ...def })
  }
  return items
}
